// SSE (text/event-stream) yardimcilari - GlowyAI streaming sohbeti icin.
// EventSource POST desteklemedigi icin fetch + ReadableStream kullanilir.

/**
 * Saf SSE ayristirici. feed(chunkString) ile parca parca besle;
 * tam bloklar icin onEvent(eventName, dataString) cagrilir.
 * - Blok ayrimi bos satir (\n\n, \r\n\r\n, \r\r)
 * - ":" ile baslayan satirlar yorumdur (ping) -> yok sayilir
 * - Cok satirli data: satirlar "\n" ile birlestirilir
 */
export function createSseParser(onEvent) {
  let buffer = '';
  const dispatch = (block) => {
    let event = 'message';
    const dataLines = [];
    let hasField = false;
    for (const rawLine of block.split('\n')) {
      const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
      if (!line || line.startsWith(':')) continue;
      const idx = line.indexOf(':');
      const field = idx === -1 ? line : line.slice(0, idx);
      let value = idx === -1 ? '' : line.slice(idx + 1);
      if (value.startsWith(' ')) value = value.slice(1);
      if (field === 'event') { event = value; hasField = true; }
      else if (field === 'data') { dataLines.push(value); hasField = true; }
    }
    if (!hasField) return; // sadece yorum/ping blogu
    onEvent(event, dataLines.join('\n'));
  };
  return {
    feed(chunk) {
      buffer += chunk;
      // Sondaki tek "\r" bir sonraki chunk'taki "\n" ile cift satir sonu olmasin diye bekletilir
      const pendingCR = buffer.endsWith('\r');
      if (pendingCR) buffer = buffer.slice(0, -1);
      buffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      if (pendingCR) buffer += '\r';
      let sep;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        dispatch(block);
      }
    },
    // Akis kapaninca kalan tamamlanmamis blogu (varsa) bosalt
    flush() {
      const rest = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
      buffer = '';
      if (rest) dispatch(rest);
    }
  };
}

const safeJson = (s) => { try { return JSON.parse(s); } catch { return null; } };

/**
 * Streaming istegi yapar. Donus: { fellBack: boolean } degil; hata sinifi ile reddeder.
 * Reddedilen Error uzerinde:
 *   err.streamFallbackOk = true  -> hic byte/delta gelmedi, eski uca dusmek guvenli
 *   err.aborted = true           -> kullanici/sayfa abort etti
 *   err.partial = true           -> delta geldikten sonra koptu (otomatik tekrar YOK)
 * Basarida done gövdesini (eski /respond JSON'u) dondurur.
 */
export async function streamChat({ url, headers, body, signal, onMeta, onStatus, onDelta, onReset, onTable }) {
  let resp;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: { ...headers, Accept: 'text/event-stream' },
      credentials: 'include',
      body: JSON.stringify(body),
      signal
    });
  } catch (e) {
    if (e && (e.name === 'AbortError' || signal?.aborted)) { const err = new Error('aborted'); err.aborted = true; throw err; }
    const err = new Error(e?.message || 'Network error');
    err.streamFallbackOk = true; // baglanti kurulamadi, sunucu istegi almadi
    throw err;
  }

  const ctype = (resp.headers.get('content-type') || '').toLowerCase();
  if (resp.status === 404 || resp.status === 405 || resp.status === 501 || resp.status === 502 || resp.status === 503 || resp.status === 504) {
    const err = new Error(`HTTP ${resp.status}`); err.streamFallbackOk = true; throw err;
  }
  if (!resp.ok) {
    // 4xx/5xx (yetki, kota vb.): eski uc ayni hatayi verir; mesaji aynen goster
    const txt = await resp.text().catch(() => '');
    const err = new Error(txt || `HTTP ${resp.status}`); throw err;
  }
  if (!ctype.includes('text/event-stream') || !resp.body || !resp.body.getReader) {
    const err = new Error('Not an event stream'); err.streamFallbackOk = true; throw err;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let doneData = null;
  let errorData = null;
  let gotDelta = false;

  const parser = createSseParser((event, data) => {
    const j = safeJson(data);
    switch (event) {
      case 'meta': onMeta && onMeta(j || {}); break;
      case 'status': onStatus && onStatus((j && j.message) || ''); break;
      case 'delta': if (j && typeof j.text === 'string') { gotDelta = true; onDelta && onDelta(j.text); } break;
      case 'reset': onReset && onReset(); break;
      case 'table': if (j && j.table && typeof j.table === 'object') { gotDelta = true; onTable && onTable(j.table); } break;
      case 'done': doneData = j; break;
      case 'error': errorData = j || { error: data }; break;
      default: break;
    }
  });

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
      if (doneData || errorData) break;
    }
    parser.feed(decoder.decode());
    parser.flush();
  } catch (e) {
    if (e && (e.name === 'AbortError' || signal?.aborted)) { const err = new Error('aborted'); err.aborted = true; throw err; }
    const err = new Error(e?.message || 'Stream interrupted');
    if (gotDelta) err.partial = true; else err.streamFallbackOk = !doneData && !errorData;
    throw err;
  } finally {
    try { reader.cancel(); } catch (_) { /* ignore */ }
  }

  if (errorData) {
    const err = new Error(errorData.error || 'Stream error');
    err.status = errorData.status;
    if (gotDelta) err.partial = true;
    throw err;
  }
  if (!doneData) {
    const err = new Error('Stream ended before completion');
    if (gotDelta) err.partial = true; else err.streamFallbackOk = true;
    throw err;
  }
  return doneData;
}

// ---------------------------------------------------------------------------
// Saf mesaj-state yardimcilari (streaming balonu). Hepsi yeni dizi dondurur.
// Akis balonu: { role:'assistant', content: yorum taslagi, tables:[...], _sid, _streaming }
// ---------------------------------------------------------------------------
const newStreamMsg = (sid) => ({ role: 'assistant', content: '', _sid: sid, _streaming: true });

export function streamAddTable(messages, sid, table) {
  if (!table) return messages;
  const idx = messages.findIndex((m) => m._sid === sid);
  if (idx === -1) return [...messages, { ...newStreamMsg(sid), tables: [table] }];
  const cur = messages[idx];
  const tables = Array.isArray(cur.tables) ? cur.tables : [];
  if (table.id != null && tables.some((t) => t && t.id === table.id)) return messages; // ayni id yok say
  const next = messages.slice();
  next[idx] = { ...cur, tables: [...tables, table] };
  return next;
}

export function streamAppendDelta(messages, sid, chunk) {
  if (!chunk) return messages;
  const idx = messages.findIndex((m) => m._sid === sid);
  if (idx === -1) return [...messages, { ...newStreamMsg(sid), content: chunk }];
  const next = messages.slice();
  next[idx] = { ...messages[idx], content: (messages[idx].content || '') + chunk };
  return next;
}

// reset: tablo varsa yalniz yorum taslagi temizlenir; yoksa balon kaldirilir (eski davranis)
export function streamReset(messages, sid) {
  const idx = messages.findIndex((m) => m._sid === sid);
  if (idx === -1) return messages;
  const cur = messages[idx];
  if (Array.isArray(cur.tables) && cur.tables.length) {
    const next = messages.slice();
    next[idx] = { ...cur, content: '' };
    return next;
  }
  return messages.filter((m) => m._sid !== sid);
}

// done: akis balonunu nihai mesajla degistir (yoksa sona ekle). tables varsa done.tables esas alinir.
export function streamFinalize(messages, sid, finalMsg) {
  const idx = messages.findIndex((m) => m._sid === sid);
  if (idx === -1) return [...messages, finalMsg];
  const next = messages.slice();
  next[idx] = finalMsg;
  return next;
}

// Cevabin son satiri "Kaynak: ..." ise ayir -> { body, source }
export function splitSourceLine(text) {
  if (typeof text !== 'string' || !text) return { body: text || '', source: null };
  const lines = text.replace(/\s+$/, '').split('\n');
  const last = lines[lines.length - 1] || '';
  if (/^[*_\s]*Kaynak\b/i.test(last)) {
    return { body: lines.slice(0, -1).join('\n').replace(/\s+$/, ''), source: last.replace(/\*\*|__/g, '').trim() };
  }
  return { body: text, source: null };
}
